import { PermissionTypes, EntityType } from "../types/permissionTypes";
import { config } from "../../config/config";

export interface GetConstantsPayload {
  categories: string[];
  system: string[];
}

export const getPermissionConstantsRequest = (payload: GetConstantsPayload) => ({
  type: PermissionTypes.GET_PERMISSION_CONSTANTS_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/permission/constants`,
        method: "POST",
        json: payload,
      },
      commit: { type: PermissionTypes.GET_PERMISSION_CONSTANTS_SUCCESS },
      rollback: { type: PermissionTypes.GET_PERMISSION_CONSTANTS_FAILURE },
    },
  },
});

export interface GetScopeProgramsPayload {
  code?: string[];
  cm_location?: { cm: string; cm_location: string[] }[];
}

export const getPermissionScopeProgramsRequest = (payload: GetScopeProgramsPayload) => ({
  type: PermissionTypes.GET_PERMISSION_SCOPE_PROGRAMS_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/permission/scope-programs`,
        method: "POST",
        json: payload,
      },
      commit: { type: PermissionTypes.GET_PERMISSION_SCOPE_PROGRAMS_SUCCESS },
      rollback: { type: PermissionTypes.GET_PERMISSION_SCOPE_PROGRAMS_FAILURE },
    },
  },
});

export const getPermissionScopeOptionsRequest = () => ({
  type: PermissionTypes.GET_PERMISSION_SCOPE_OPTIONS_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/permission/scope-options`,
        method: "GET",
      },
      commit: { type: PermissionTypes.GET_PERMISSION_SCOPE_OPTIONS_SUCCESS },
      rollback: { type: PermissionTypes.GET_PERMISSION_SCOPE_OPTIONS_FAILURE },
    },
  },
});

export const getPermissionMeRequest = (system: string) => ({
  type: PermissionTypes.GET_PERMISSION_ME_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/permission/me?system=${system}`,
        method: "GET",
      },
      commit: { type: PermissionTypes.GET_PERMISSION_ME_SUCCESS },
      rollback: { type: PermissionTypes.GET_PERMISSION_ME_FAILURE },
    },
  },
});

export const getPermissionRolesRequest = (system: string) => ({
  type: PermissionTypes.GET_PERMISSION_ROLES_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/permission/roles?system=${system}`,
        method: "GET",
      },
      commit: { type: PermissionTypes.GET_PERMISSION_ROLES_SUCCESS },
      rollback: { type: PermissionTypes.GET_PERMISSION_ROLES_FAILURE },
    },
  },
});

export interface GetAssignmentsParams {
  page?: number;
  page_size?: number;
  system?: string;
  search?: string;
}

export const getPermissionAssignmentsRequest = (params?: GetAssignmentsParams) => {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set("page", String(params.page));
  if (params?.page_size) queryParams.set("page_size", String(params.page_size));
  if (params?.system && params.system !== "All") queryParams.set("system", params.system);
  if (params?.search) queryParams.set("search", params.search);
  const queryString = queryParams.toString();
  const url = `${(config as any).BASE_URL}/permission/assignments${queryString ? `?${queryString}` : ""}`;

  return {
    type: PermissionTypes.GET_PERMISSION_ASSIGNMENTS_REQUEST,
    http: {
      cognito: {
        effect: {
          url,
          method: "GET",
        },
        commit: { type: PermissionTypes.GET_PERMISSION_ASSIGNMENTS_SUCCESS },
        rollback: { type: PermissionTypes.GET_PERMISSION_ASSIGNMENTS_FAILURE },
      },
    },
  };
};

/**
 * Upsert (Create / Update) a permission role.
 * - Create: omit role_id in payload
 * - Update: include role_id in payload
 */
export interface UpsertRolePayload {
  role_id?: string;
  system?: string;
  role_name?: string;
  description?: string;
  permissions: { feature: string; actions: string[] }[];
  status?: string;
  version?: number;
}

export const upsertPermissionRoleRequest = (payload: UpsertRolePayload) => ({
  type: PermissionTypes.UPSERT_PERMISSION_ROLE_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/permission/roles`,
        method: "POST",
        json: payload,
      },
      commit: { type: PermissionTypes.UPSERT_PERMISSION_ROLE_SUCCESS },
      rollback: { type: PermissionTypes.UPSERT_PERMISSION_ROLE_FAILURE },
    },
  },
});

export interface UpsertAssignmentPayload {
  assignment_id?: string;
  system?: string;
  entity_type?: EntityType;
  entity?: string;
  entity_display_name?: string;
  roles: string[];
  operations: string[];
  cm_locations: { cm: string; location: string[] }[];
  programs: string[];
  status?: string;
  version?: number;
}

export const upsertPermissionAssignmentRequest = (payload: UpsertAssignmentPayload) => ({
  type: PermissionTypes.UPSERT_PERMISSION_ASSIGNMENT_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/permission/assignments`,
        method: "POST",
        json: payload,
      },
      commit: { type: PermissionTypes.UPSERT_PERMISSION_ASSIGNMENT_SUCCESS },
      rollback: { type: PermissionTypes.UPSERT_PERMISSION_ASSIGNMENT_FAILURE },
    },
  },
});

export interface ChangeAssignmentStatusPayload {
  type: "active" | "inactive" | "deleted";
  ids: string[];
  versions: number[];
}

export const changePermissionAssignmentStatusRequest = (payload: ChangeAssignmentStatusPayload) => ({
  type: PermissionTypes.CHANGE_PERMISSION_ASSIGNMENT_STATUS_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/permission/assignments/status`,
        method: "POST",
        json: payload,
      },
      commit: { type: PermissionTypes.CHANGE_PERMISSION_ASSIGNMENT_STATUS_SUCCESS },
      rollback: { type: PermissionTypes.CHANGE_PERMISSION_ASSIGNMENT_STATUS_FAILURE },
    },
  },
});

export const deletePermissionAssignmentsRequest = (assignmentIds: string[]) => ({
  type: PermissionTypes.DELETE_PERMISSION_ASSIGNMENTS_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/permission/assignments/delete`,
        method: "POST",
        json: { assignment_ids: assignmentIds },
      },
      commit: { type: PermissionTypes.DELETE_PERMISSION_ASSIGNMENTS_SUCCESS },
      rollback: { type: PermissionTypes.DELETE_PERMISSION_ASSIGNMENTS_FAILURE },
    },
  },
});

export interface ChangeRoleStatusPayload {
  type: "active" | "inactive" | "deleted";
  ids: string[];
  versions: number[];
}

export const changePermissionRoleStatusRequest = (payload: ChangeRoleStatusPayload) => ({
  type: PermissionTypes.CHANGE_PERMISSION_ROLE_STATUS_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/permission/roles/status`,
        method: "POST",
        json: payload,
      },
      commit: { type: PermissionTypes.CHANGE_PERMISSION_ROLE_STATUS_SUCCESS },
      rollback: { type: PermissionTypes.CHANGE_PERMISSION_ROLE_STATUS_FAILURE },
    },
  },
});

/**
 * Delete a permission role by role_id.
 * DELETE /permission/roles/{role_id}
 */
export const deletePermissionRoleRequest = (roleIds: string[]) => ({
  type: PermissionTypes.DELETE_PERMISSION_ROLE_REQUEST,
  http: {
    cognito: {
      effect: {
        url: `${(config as any).BASE_URL}/permission/roles/delete`,
        method: "POST",
        json: { role_ids: roleIds },
      },
      commit: { type: PermissionTypes.DELETE_PERMISSION_ROLE_SUCCESS },
      rollback: { type: PermissionTypes.DELETE_PERMISSION_ROLE_FAILURE },
    },
  },
});
